import { yupResolver } from "@hookform/resolvers/yup";
import clsx from "clsx";
import { FC, useState } from "react";
import { FormProvider, Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGoogleServices } from "@ryde/redux";
import {
  AreaApi,
  cleanSecondaryText,
  errors,
  IPlacePrediction,
  Log,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { MuiButton } from "../../../../controls";
import { DropdownBoundTemplate } from "../../../../dynamic-forms";
import { Address, StaticMap } from "../../../map";
import {
  IQuickStationFormModel,
  IRideAddQuickStationForm,
  QuickStationFormFields,
  rideAddQuickStationFormValidationRules,
} from "./ride-add-quick-station-form.models";

export const RideAddQuickStationForm: FC<IRideAddQuickStationForm> = ({
  className,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();

  const methods = useForm<IQuickStationFormModel>({
    resolver: yupResolver(rideAddQuickStationFormValidationRules) as Resolver<
      IQuickStationFormModel,
      unknown
    >,
    defaultValues: {
      [QuickStationFormFields.address]: undefined,
      [QuickStationFormFields.areaId]: undefined,
      [QuickStationFormFields.coordinate]: undefined,
    },
  });
  const {
    getValues,
    setValue,
    watch,
    formState: { isValid, dirtyFields },
  } = methods;
  const { getCoordinateByPlaceId } = useGoogleServices();

  const [isAreaSuccess, setIsAreaSuccess] = useState<boolean>(false);
  const watchedAddress = watch(QuickStationFormFields.address);
  const watchedCoordinate = watch(QuickStationFormFields.coordinate);

  const handleAddressChange = async (
    newAddress: IPlacePrediction | null,
  ): Promise<void> => {
    if (!newAddress) {
      Log.error("No address");

      return;
    }

    setValue(QuickStationFormFields.address, newAddress, { shouldDirty: true });

    if (!newAddress?.placeId) {
      Log.error("No place_id");
      return;
    }

    try {
      const coordinateResult = await getCoordinateByPlaceId(newAddress.placeId);

      if (!coordinateResult.success) {
        warnAndToastSimpleResult(coordinateResult, t);
        return;
      }

      if (!coordinateResult.data) {
        Log.error("No coordinates");
        return;
      }

      setValue(QuickStationFormFields.coordinate, coordinateResult.data, {
        shouldValidate: true,
      });
      Log.debug("Coordinate fetched for address:", coordinateResult.data);

      if (!coordinateResult.data?.lat) {
        Log.error("Coordinate lat is missed");
        return;
      }

      if (!coordinateResult.data?.lng) {
        Log.error("Coordinate lng is missed");
        return;
      }

      const areas = await AreaApi.findByCoordinates({
        lat: coordinateResult.data.lat,
        lng: coordinateResult.data.lng,
      });

      if (!areas?.length) {
        setIsAreaSuccess(false);
        Log.infoAndToast(t("ride.noAreasFondByCoordinatesChooseFromDropdown"));
        return;
      } else {
        setIsAreaSuccess(true);
        setValue(QuickStationFormFields.areaId, areas[0].id, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    } catch (err) {
      setIsAreaSuccess(false);

      Log.warnAndToast(t(errors.common.technicalProblemOccurred), err);
    }
  };

  const handleSave = (): void => {
    const formData = getValues();

    if (!formData.address?.text?.text) {
      Log.warnAndToast(t("general.PLEASE_FILL_REQUIRED_FIELDS"));
      return;
    }

    onSave({
      address: cleanSecondaryText(formData.address.text?.text),
      areaId: formData.areaId,
      coordinate: formData.coordinate,
    });
  };

  return (
    <div className={clsx("add-station__add-new-station-form", className)}>
      <FormProvider {...methods}>
        <Address
          value={(watchedAddress as IPlacePrediction) ?? null}
          onChange={handleAddressChange}
          label={t("station.formLabel.locationInfo")}
          error={dirtyFields.address && !watchedAddress?.text?.text}
          size="small"
        />

        <DropdownBoundTemplate
          name={QuickStationFormFields.areaId}
          size="small"
          labelTranslated={t("station.formLabel.areaId")}
          formAttributes={{
            dropItemsSource: "area",
            dropItemsDisableAdd: true,
            disabled: !watchedAddress?.text?.text || isAreaSuccess,
          }}
        />

        <div className="add-station__add-new-station-form-static-map-wrapper">
          <StaticMap coordinate={watchedCoordinate} />
        </div>

        <div className="add-station__add-new-station-form-controls">
          <MuiButton onClick={onClose} muiVariant="text">
            {t("general.CANCEL")}
          </MuiButton>
          <MuiButton onClick={handleSave} muiVariant="text" disabled={!isValid}>
            {t("general.SAVE")}
          </MuiButton>
        </div>
      </FormProvider>
    </div>
  );
};
